import { MerrySidePopup } from "@/shared/ui/MerrySidePopup";
import type { ReactNode } from "react";

import { cn } from "@/shared/lib/cn";
import { MerryAuroraBackdrop } from "@/shared/ui/MerryAuroraPage";
import {
  getMerryDesktopShellSourceNodeIds,
  type MerryDesktopShellMode,
  type MerryDesktopShellSideDrawerVariant,
} from "@/shared/ui/merryDesktopShellVariants";

export interface MerryDesktopShellProps {
  auxiliaryRail?: ReactNode;
  bottomBar?: ReactNode;
  children: ReactNode;
  className?: string;
  componentPanel?: ReactNode;
  componentPanelTools?: ReactNode;
  footer?: ReactNode;
  isComponentPanelOpen?: boolean;
  leftNavigation: ReactNode;
  localNavigation?: ReactNode;
  mainLabel?: string;
  mode?: MerryDesktopShellMode;
  overlay?: ReactNode;
  overlayLabel?: string;
  panelLabel?: string;
  rightRail?: ReactNode;
  rightRailLabel?: string;
  sideDrawer?: ReactNode;
  sideDrawerLabel?: string;
  sideDrawerVariant?: MerryDesktopShellSideDrawerVariant;
  testId?: string;
  title?: ReactNode;
  topNavigation: ReactNode;
}

export function MerryDesktopShell({
  auxiliaryRail,
  bottomBar,
  children,
  className,
  componentPanel,
  componentPanelTools,
  footer,
  isComponentPanelOpen = false,
  leftNavigation,
  localNavigation,
  mainLabel = "页面内容",
  mode = "content",
  overlay,
  overlayLabel = "页面通知",
  panelLabel = "组件面板",
  rightRail,
  rightRailLabel = "页面组件",
  sideDrawer,
  sideDrawerLabel = "侧边抽屉",
  sideDrawerVariant = "default",
  testId,
  title,
  topNavigation,
}: MerryDesktopShellProps) {
  const hasPanelContent = Boolean(componentPanel || componentPanelTools);
  const hasWorkspaceBottom = Boolean(bottomBar || footer);

  return (
    <div
      className={cn("merry-desktop-shell", className)}
      data-component-panel-open={isComponentPanelOpen ? "true" : "false"}
      data-desktop-shell-mode={mode}
      data-figma-source-node-ids={getMerryDesktopShellSourceNodeIds(mode)}
      data-side-drawer-open={sideDrawer ? "true" : "false"}
      data-side-drawer-variant={sideDrawerVariant}
      data-testid={testId}
    >
      <MerryAuroraBackdrop className="merry-desktop-shell__backdrop" />

      <div className="merry-desktop-shell__top-navigation">
        {topNavigation}
      </div>

      <div className="merry-desktop-shell__body">
        <div className="merry-desktop-shell__left-navigation">
          {leftNavigation}
        </div>

        {auxiliaryRail ? (
          <div
            className="merry-desktop-shell__auxiliary-rail"
            data-shell-part="auxiliary-rail"
          >
            {auxiliaryRail}
          </div>
        ) : null}

        <main
          aria-label={mainLabel}
          className="merry-desktop-shell__main-canvas"
          data-shell-part="main-canvas"
        >
          <div
            className="merry-desktop-shell__page-frame"
            data-shell-has-local-navigation={
              localNavigation ? "true" : "false"
            }
            data-shell-has-title={title ? "true" : "false"}
            data-shell-part="page-frame"
          >
            {mode === "content" ? (
              <>
                {title ? (
                  <div
                    className="merry-desktop-shell__title"
                    data-shell-part="title"
                  >
                    {title}
                  </div>
                ) : null}

                <div className="merry-desktop-shell__content-grid">
                  {localNavigation ? (
                    <div
                      className="merry-desktop-shell__local-navigation"
                      data-shell-part="local-navigation"
                    >
                      {localNavigation}
                    </div>
                  ) : null}

                  <div
                    className="merry-desktop-shell__content-scroll"
                    data-shell-part="content-scroll"
                  >
                    {children}
                  </div>
                </div>
              </>
            ) : mode === "workspace" ? (
              <div className="merry-desktop-shell__workspace">
                <div
                  className="merry-desktop-shell__workspace-scroll"
                  data-shell-part="workspace-scroll"
                >
                  {children}
                </div>

                {hasWorkspaceBottom ? (
                  <div
                    className="merry-desktop-shell__workspace-bottom"
                    data-shell-part="workspace-bottom"
                  >
                    {bottomBar ? (
                      <div className="merry-desktop-shell__bottom-bar">
                        {bottomBar}
                      </div>
                    ) : null}
                    {footer ? (
                      <div className="merry-desktop-shell__footer">
                        {footer}
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            ) : (
              <div
                className="merry-desktop-shell__canvas"
                data-shell-part="canvas"
              >
                {children}
              </div>
            )}
          </div>
        </main>

        {rightRail ? (
          <aside
            aria-label={rightRailLabel}
            className="merry-desktop-shell__right-rail"
            data-shell-part="right-rail"
          >
            {rightRail}
          </aside>
        ) : null}

        {overlay ? (
          <aside
            aria-label={overlayLabel}
            className="merry-desktop-shell__overlay"
            data-shell-part="overlay"
          >
            {overlay}
          </aside>
        ) : null}

        {isComponentPanelOpen && hasPanelContent ? (
          <aside
            aria-label={panelLabel}
            className="merry-desktop-shell__component-panel"
            data-shell-part="component-panel"
          >
            {componentPanelTools ? (
              <div
                className="merry-desktop-shell__component-panel-tools"
                data-shell-part="component-panel-tools"
              >
                {componentPanelTools}
              </div>
            ) : null}
            {componentPanel ? (
              <div className="merry-desktop-shell__component-panel-card">
                {componentPanel}
              </div>
            ) : null}
          </aside>
        ) : null}
      </div>

      <MerrySidePopup open={Boolean(sideDrawer)} width="wide" label={sideDrawerLabel}
          className="merry-desktop-shell__side-drawer"
          data-figma-node-id={
            sideDrawerVariant === "store-overview-with-chat"
              ? "738:114645"
              : sideDrawerVariant === "store-overview"
                ? "736:110402"
                : "719:80608"
          }
          data-side-drawer-variant={sideDrawerVariant}
          data-shell-part="side-drawer"
        >
          {sideDrawer}
      </MerrySidePopup>
    </div>
  );
}
